/**
 * Neptune brand assets — pure HTML wordmark, transparent trident PNG, zero raster baggage.
 *
 *   - <NeptuneMark>     icon only (holographic trident PNG)
 *   - <NeptuneLogo>     trident + "Neptune" wordmark side by side
 *   - <NeptuneWordmark> "Neptune" rendered as styled HTML text — uses the same display
 *                       font (Outfit) as the rest of the site, with a chrome silver gradient
 *                       via -webkit-background-clip.
 */

const TRIDENT = "/neptune-trident-v5.png";
const TRIDENT_W = 668;
const TRIDENT_H = 1024;

export function NeptuneMark({ size = 24, className = "" }) {
  const w = Math.round(size * (TRIDENT_W / TRIDENT_H));
  return (
    <img
      src={TRIDENT}
      alt=""
      aria-hidden="true"
      width={w}
      height={size}
      draggable={false}
      decoding="async"
      loading="eager"
      className={`inline-block align-middle ${className}`}
      style={{
        width: `${w}px`,
        height: `${size}px`,
        userSelect: "none",
        objectFit: "contain",
      }}
    />
  );
}

/**
 * "Neptune" wordmark — plain HTML text styled with a silver chrome gradient.
 * Sizes itself precisely to the rendered text (no extra padding).
 */
export function NeptuneWordmark({ height = 24, className = "" }) {
  // We size the text via fontSize ~= height (visual cap-height ≈ 0.72 of fontSize for Outfit).
  const fontSize = Math.round(height / 0.72);
  return (
    <span
      role="img"
      aria-label="Neptune"
      className={`font-display inline-block align-middle ${className}`}
      style={{
        fontSize: `${fontSize}px`,
        lineHeight: 1,
        fontWeight: 600,
        letterSpacing: "-0.04em",
        // Chrome silver gradient — works in WebKit / Blink / Firefox.
        background: "linear-gradient(180deg, #FFFFFF 0%, #E8ECF2 55%, #B7C0CC 100%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
        userSelect: "none",
      }}
    >
      Neptune
    </span>
  );
}

export function NeptuneLogo({ height = 26, className = "" }) {
  // Trident is portrait; sizing by height keeps the silhouette honest.
  // Match the trident's cap-height to the wordmark's cap-height so the
  // two read as one continuous mark instead of "icon next to text".
  // Wordmark cap-height ≈ 0.72 × fontSize ≈ 0.72 × (height / 0.72) = height.
  // We bump the trident slightly above the cap-height (×1.4) so its
  // ornamental flourishes have presence, then breathe with a comfortable
  // gap so the mark isn't visually fused.
  const tridentSize = Math.round(height * 1.4);
  return (
    <span
      role="img"
      aria-label="Neptune"
      className={`inline-flex items-center ${className}`}
      style={{ gap: `${Math.round(height * 0.32)}px`, height: `${tridentSize}px` }}
    >
      <NeptuneMark size={tridentSize} />
      <NeptuneWordmark height={height} />
    </span>
  );
}

export default NeptuneMark;
